import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Share2, HelpCircle, Plus, Copy } from 'lucide-react';
import Button from 'components/Button';
import Toast from 'components/Toast';
import { ROUTES } from 'utils/constants';
import {
  IngredientInput,
  AdditionalCosts,
  AdditionalCostItem,
  YieldInfo,
  MethodOfSale,
  UnitType,
} from 'types/costing';
import styles from './CostingCalculator.module.scss';

const UNIT_OPTIONS: { value: UnitType; label: string }[] = [
  { value: 'kg', label: 'Kilogram (kg)' },
  { value: 'g', label: 'Gram (g)' },
  { value: 'lb', label: 'Pound (lb)' },
  { value: 'l', label: 'Liter (L)' },
  { value: 'ml', label: 'Milliliter (mL)' },
  { value: 'pcs', label: 'Piece (pcs)' },
];

const LB_TO_G = 453.592;
const STORAGE_KEY = 'costing_calculator_data';
const MAX_STRING_LENGTH = 200;
const MAX_INGREDIENTS = 100;

// Security: Sanitize string inputs - prevents XSS and injection attacks
const sanitizeString = (
  input: unknown,
  maxLength = MAX_STRING_LENGTH,
): string => {
  if (typeof input !== 'string') return '';
  // Remove potentially dangerous characters and patterns
  return input
    .replace(/[<>"'`]/g, '') // Remove HTML/script delimiters
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers (onclick, onerror, etc.)
    .replace(/script/gi, '') // Remove script tags
    .replace(/iframe/gi, '') // Remove iframe tags
    .replace(/object/gi, '') // Remove object tags
    .replace(/embed/gi, '') // Remove embed tags
    .replace(/data:/gi, '') // Remove data: protocol
    .replace(/vbscript:/gi, '') // Remove vbscript: protocol
    .slice(0, maxLength)
    .trim();
};

// Security: Validate and sanitize numeric values
const sanitizeNumber = (
  input: unknown,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
): number => {
  const num = typeof input === 'number' ? input : Number(input);
  if (Number.isNaN(num) || !Number.isFinite(num)) return 0;
  return Math.max(min, Math.min(max, num));
};

// Security: Validate unit type
const isValidUnit = (unit: unknown): unit is UnitType => {
  return (
    typeof unit === 'string' &&
    ['kg', 'g', 'lb', 'l', 'ml', 'pcs'].includes(unit)
  );
};

// Security: Validate cost mode
const isValidCostMode = (mode: unknown): mode is 'unit' | 'total' => {
  return mode === 'unit' || mode === 'total';
};

// Security: Validate and sanitize ingredient
const sanitizeIngredient = (input: unknown): IngredientInput | null => {
  if (!input || typeof input !== 'object') return null;

  const obj = input as Record<string, unknown>;
  const id = typeof obj.id === 'string' ? obj.id : crypto.randomUUID();
  const name = sanitizeString(obj.name);
  const quantity = sanitizeNumber(obj.quantity);
  const unitCost = sanitizeNumber(obj.unitCost);
  const totalCost = sanitizeNumber(obj.totalCost);
  const unit = isValidUnit(obj.unit) ? obj.unit : 'kg';
  const costMode = isValidCostMode(obj.costMode) ? obj.costMode : 'unit';

  return {
    id,
    name,
    quantity,
    unitCost,
    unit,
    totalCost,
    costMode,
  };
};

// Security: Validate and sanitize additional cost item
const sanitizeAdditionalCostItem = (
  input: unknown,
): AdditionalCostItem | null => {
  if (!input || typeof input !== 'object') return null;
  const obj = input as Record<string, unknown>;
  const id =
    typeof obj.id === 'string'
      ? obj.id
      : crypto.randomUUID?.() || Date.now().toString();
  const name = sanitizeString(obj.name, 100);
  const amount = sanitizeNumber(obj.amount);
  return { id, name, amount };
};

// Security: Validate and sanitize method of sale
const sanitizeMethodOfSale = (input: unknown): MethodOfSale | null => {
  if (!input || typeof input !== 'object') return null;
  const obj = input as Record<string, unknown>;
  const id =
    typeof obj.id === 'string'
      ? obj.id
      : crypto.randomUUID?.() || Date.now().toString();
  const quantity = sanitizeNumber(obj.quantity, 0);
  const unit = isValidUnit(obj.unit) ? obj.unit : 'pcs';
  const label = sanitizeString(obj.label, 50);
  return { id, quantity, unit, label: label || undefined };
};

// Security: Validate and sanitize yield info
const sanitizeYieldInfo = (input: unknown): YieldInfo => {
  if (!input || typeof input !== 'object') {
    return { batchYield: 1, yieldUnit: 'unit' };
  }
  const obj = input as Record<string, unknown>;
  return {
    batchYield: sanitizeNumber(obj.batchYield, 0),
    yieldUnit: sanitizeString(obj.yieldUnit, 50) || 'unit',
  };
};

// Security: Validate and sanitize additional costs (supports old and new format)
const sanitizeAdditionalCosts = (input: unknown): AdditionalCosts => {
  if (!input || typeof input !== 'object') {
    return {
      items: [],
      yield: { batchYield: 1, yieldUnit: 'unit' },
      methodsOfSale: [],
    };
  }

  const obj = input as Record<string, unknown>;

  // Check if old format (backward compatibility)
  if ('packaging' in obj || 'labor' in obj || 'overhead' in obj) {
    const oldItems: AdditionalCostItem[] = [];
    if (obj.packaging && Number(obj.packaging) > 0) {
      oldItems.push({
        id: crypto.randomUUID?.() || '1',
        name: 'Packaging',
        amount: sanitizeNumber(obj.packaging),
      });
    }
    if (obj.labor && Number(obj.labor) > 0) {
      oldItems.push({
        id: crypto.randomUUID?.() || '2',
        name: 'Labor',
        amount: sanitizeNumber(obj.labor),
      });
    }
    if (obj.overhead && Number(obj.overhead) > 0) {
      oldItems.push({
        id: crypto.randomUUID?.() || '3',
        name: 'Overhead',
        amount: sanitizeNumber(obj.overhead),
      });
    }
    return {
      items: oldItems,
      yield: {
        batchYield: sanitizeNumber(obj.batchYield, 0) || 1,
        yieldUnit: sanitizeString(obj.yieldUnit, 50) || 'unit',
      },
      methodsOfSale: [],
    };
  }

  // New format
  const items =
    Array.isArray(obj.items) && obj.items.length <= 50
      ? obj.items
          .map((item: unknown) => sanitizeAdditionalCostItem(item))
          .filter(
            (item: AdditionalCostItem | null): item is AdditionalCostItem =>
              item !== null,
          )
      : [];

  const yieldInfo = obj.yield
    ? sanitizeYieldInfo(obj.yield)
    : { batchYield: 1, yieldUnit: 'unit' };

  const methodsOfSale =
    Array.isArray(obj.methodsOfSale) && obj.methodsOfSale.length <= 20
      ? obj.methodsOfSale
          .map((mos: unknown) => sanitizeMethodOfSale(mos))
          .filter(
            (mos: MethodOfSale | null): mos is MethodOfSale => mos !== null,
          )
      : [];

  return {
    items,
    yield: yieldInfo,
    methodsOfSale,
  };
};

// Load from localStorage
const loadFromStorage = (): {
  ingredients: IngredientInput[];
  additionalCosts: AdditionalCosts;
} | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return null;

    const ingredients =
      Array.isArray(parsed.ingredients) &&
      parsed.ingredients.length > 0 &&
      parsed.ingredients.length <= MAX_INGREDIENTS
        ? (parsed.ingredients as unknown[])
            .map((item: unknown) => sanitizeIngredient(item))
            .filter(
              (ing: IngredientInput | null): ing is IngredientInput =>
                ing !== null,
            )
        : null;

    if (!ingredients || ingredients.length === 0) return null;

    const additionalCosts = sanitizeAdditionalCosts(parsed.additionalCosts);

    return { ingredients, additionalCosts };
  } catch {
    return null;
  }
};

// Save to localStorage
const saveToStorage = (
  ingredients: IngredientInput[],
  additionalCosts: AdditionalCosts,
): void => {
  try {
    const data = {
      ingredients,
      additionalCosts,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Silently fail if storage is unavailable
  }
};

// Encode data to URL-safe string
const encodeData = (
  ingredients: IngredientInput[],
  additionalCosts: AdditionalCosts,
): string => {
  try {
    const data = {
      i: ingredients.map((ing: IngredientInput) => ({
        n: ing.name,
        q: ing.quantity,
        u: ing.unit,
        uc: ing.unitCost,
        tc: ing.totalCost,
        m: ing.costMode,
      })),
      a: {
        items: additionalCosts.items.map((item) => ({
          n: item.name,
          a: item.amount,
        })),
        y: {
          by: additionalCosts.yield.batchYield,
          yu: additionalCosts.yield.yieldUnit,
        },
        mos: additionalCosts.methodsOfSale.map((mos) => ({
          q: mos.quantity,
          u: mos.unit,
          l: mos.label,
        })),
      },
    };
    const json = JSON.stringify(data);
    return encodeURIComponent(json);
  } catch {
    return '';
  }
};

// Decode data from URL-safe string with security validation
const decodeData = (
  encoded: string,
): {
  ingredients: IngredientInput[];
  additionalCosts: AdditionalCosts;
} | null => {
  try {
    // Validate encoded string length to prevent DoS
    if (encoded.length > 10000) return null; // Reasonable limit for URL params

    const json = decodeURIComponent(encoded);
    // Additional security: validate JSON doesn't contain dangerous patterns
    if (/<script|javascript:|on\w+\s*=|data:|vbscript:/gi.test(json)) {
      return null;
    }

    const parsed = JSON.parse(json) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return null;

    if (!Array.isArray(parsed.i) || parsed.i.length === 0) return null;
    if (parsed.i.length > MAX_INGREDIENTS) return null;

    const ingredients: IngredientInput[] = [];
    for (const item of parsed.i as unknown[]) {
      if (!item || typeof item !== 'object') continue;
      const itemObj = item as Record<string, unknown>;

      const name = sanitizeString(itemObj.n);
      const quantity = sanitizeNumber(itemObj.q);
      const unitCost = sanitizeNumber(itemObj.uc);
      const totalCost = sanitizeNumber(itemObj.tc);
      const unit = isValidUnit(itemObj.u) ? itemObj.u : 'kg';
      const costMode = isValidCostMode(itemObj.m) ? itemObj.m : 'unit';

      ingredients.push({
        id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
        name,
        quantity,
        unitCost,
        unit,
        totalCost,
        costMode,
      });
    }

    if (ingredients.length === 0) return null;

    // Handle both old and new format
    let additionalCosts: AdditionalCosts;
    if (parsed.a && typeof parsed.a === 'object') {
      const aObj = parsed.a as Record<string, unknown>;
      // Check for old format
      if ('p' in aObj || 'l' in aObj || 'o' in aObj) {
        const oldItems: AdditionalCostItem[] = [];
        if (aObj.p && Number(aObj.p) > 0) {
          oldItems.push({
            id: crypto.randomUUID?.() || '1',
            name: 'Packaging',
            amount: sanitizeNumber(aObj.p),
          });
        }
        if (aObj.l && Number(aObj.l) > 0) {
          oldItems.push({
            id: crypto.randomUUID?.() || '2',
            name: 'Labor',
            amount: sanitizeNumber(aObj.l),
          });
        }
        if (aObj.o && Number(aObj.o) > 0) {
          oldItems.push({
            id: crypto.randomUUID?.() || '3',
            name: 'Overhead',
            amount: sanitizeNumber(aObj.o),
          });
        }
        additionalCosts = {
          items: oldItems,
          yield: {
            batchYield: sanitizeNumber(aObj.y, 0) || 1,
            yieldUnit: sanitizeString(aObj.yu, 50) || 'unit',
          },
          methodsOfSale: [],
        };
      } else if (aObj.items || aObj.y || aObj.mos) {
        // New format from URL
        const items: AdditionalCostItem[] = [];
        if (Array.isArray(aObj.items)) {
          for (const item of aObj.items as unknown[]) {
            if (item && typeof item === 'object') {
              const itemObj = item as Record<string, unknown>;
              const name = sanitizeString(itemObj.n, 100);
              const amount = sanitizeNumber(itemObj.a);
              if (name) {
                items.push({
                  id: crypto.randomUUID?.() || Date.now().toString(),
                  name,
                  amount,
                });
              }
            }
          }
        }

        const yieldInfo: YieldInfo =
          aObj.y && typeof aObj.y === 'object'
            ? {
                batchYield:
                  sanitizeNumber((aObj.y as Record<string, unknown>).by, 0) ||
                  1,
                yieldUnit:
                  sanitizeString((aObj.y as Record<string, unknown>).yu, 50) ||
                  'unit',
              }
            : { batchYield: 1, yieldUnit: 'unit' };

        const methodsOfSale: MethodOfSale[] = [];
        if (Array.isArray(aObj.mos)) {
          for (const mos of aObj.mos as unknown[]) {
            if (mos && typeof mos === 'object') {
              const mosObj = mos as Record<string, unknown>;
              const quantity = sanitizeNumber(mosObj.q, 0);
              const unit = isValidUnit(mosObj.u) ? mosObj.u : 'pcs';
              const label = sanitizeString(mosObj.l, 50);
              methodsOfSale.push({
                id: crypto.randomUUID?.() || Date.now().toString(),
                quantity,
                unit,
                label: label || undefined,
              });
            }
          }
        }

        additionalCosts = { items, yield: yieldInfo, methodsOfSale };
      } else {
        // Fallback to sanitize function
        additionalCosts = sanitizeAdditionalCosts(aObj);
      }
    } else {
      additionalCosts = {
        items: [],
        yield: { batchYield: 1, yieldUnit: 'unit' },
        methodsOfSale: [],
      };
    }

    return { ingredients, additionalCosts };
  } catch {
    return null;
  }
};

const getConversionHint = (quantity: number, unit: UnitType) => {
  if (quantity <= 0) return '';
  if (unit === 'g' && quantity >= 1000) {
    return `≈ ${(quantity / 1000).toFixed(2)} kg`;
  }
  if (unit === 'kg' && quantity < 1) {
    return `≈ ${(quantity * 1000).toFixed(0)} g`;
  }
  if (unit === 'lb') {
    return `≈ ${(quantity * LB_TO_G).toFixed(0)} g ( ${(quantity * 0.453592).toFixed(2)} kg )`;
  }
  if (unit === 'ml' && quantity >= 1000) {
    return `≈ ${(quantity / 1000).toFixed(2)} L`;
  }
  if (unit === 'l' && quantity < 1) {
    return `≈ ${(quantity * 1000).toFixed(0)} mL`;
  }
  return '';
};

const createIngredient = (): IngredientInput => ({
  id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
  name: '',
  quantity: 0,
  unitCost: 0,
  unit: 'kg',
  totalCost: 0,
  costMode: 'unit',
});

const createAdditionalCostItem = (): AdditionalCostItem => ({
  id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
  name: '',
  amount: 0,
});

const createMethodOfSale = (): MethodOfSale => ({
  id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
  quantity: 1,
  unit: 'pcs',
  label: undefined,
});

const CostingCalculator = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [ingredients, setIngredients] = useState<IngredientInput[]>(() => {
    // Try to load from URL params first
    const urlData = searchParams.get('data');
    if (urlData) {
      const decoded = decodeData(urlData);
      if (decoded) return decoded.ingredients;
    }
    // Fall back to localStorage
    const stored = loadFromStorage();
    if (stored) return stored.ingredients;
    // Default
    return [createIngredient()];
  });
  const [additionalCosts, setAdditionalCosts] = useState<AdditionalCosts>(
    () => {
      // Try to load from URL params first
      const urlData = searchParams.get('data');
      if (urlData) {
        const decoded = decodeData(urlData);
        if (decoded) return decoded.additionalCosts;
      }
      // Fall back to localStorage
      const stored = loadFromStorage();
      if (stored) return stored.additionalCosts;
      // Default
      return {
        items: [],
        yield: { batchYield: 1, yieldUnit: 'unit' },
        methodsOfSale: [],
      };
    },
  );
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [feedback, setFeedback] = useState('');

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Save to localStorage whenever data changes
  useEffect(() => {
    saveToStorage(ingredients, additionalCosts);
  }, [ingredients, additionalCosts]);

  // Fallback clipboard copy for older browsers
  const fallbackCopyToClipboard = useCallback(
    (text: string) => {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          showToast('Link copied to clipboard', 'success');
        } else {
          showToast('Failed to copy link. Please copy manually.', 'error');
        }
      } catch {
        showToast('Failed to copy link. Please copy manually.', 'error');
      }
    },
    [showToast],
  );

  // Copy to clipboard with fallback
  const copyToClipboard = useCallback(
    (text: string) => {
      // Use modern clipboard API with fallback
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            showToast('Link copied to clipboard', 'success');
          })
          .catch(() => {
            // Fallback for older browsers
            fallbackCopyToClipboard(text);
          });
      } else {
        fallbackCopyToClipboard(text);
      }
    },
    [showToast, fallbackCopyToClipboard],
  );

  // Feedback functionality - opens email client with feedback
  const handleSendFeedback = useCallback(() => {
    const email = 'abraham.guerrero@abrasoft.com';
    const subject = encodeURIComponent('Costing Calculator Feedback');
    const body = encodeURIComponent(
      feedback.trim() ||
        'Please share your feedback, suggestions, or report any problems you encountered.',
    );
    const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
  }, [feedback]);

  // Copy email address to clipboard
  const handleCopyEmail = useCallback(() => {
    const email = 'abraham.guerrero@abrasoft.com';
    copyToClipboard(email);
  }, [copyToClipboard]);

  // Share functionality - generates shareable URL with encoded data
  const handleShare = useCallback(() => {
    const encoded = encodeData(ingredients, additionalCosts);
    if (!encoded) {
      showToast('Failed to encode data for sharing', 'error');
      return;
    }

    // Use the route constant for consistency
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}${ROUTES.TOOLS.COSTING_CALCULATOR}?data=${encoded}`;

    // Check URL length (some browsers have limits)
    if (shareUrl.length > 2000) {
      showToast(
        'Data too large to share via URL. Please reduce the number of ingredients.',
        'error',
      );
      return;
    }

    // Try native share API first (mobile devices)
    if (navigator.share) {
      navigator
        .share({
          title: 'Costing Calculator',
          text: 'Check out my food costing calculation',
          url: shareUrl,
        })
        .then(() => {
          showToast('Shared successfully', 'success');
        })
        .catch(() => {
          // Fall back to clipboard if share is cancelled
          copyToClipboard(shareUrl);
        });
    } else {
      // Fall back to clipboard copy
      copyToClipboard(shareUrl);
    }
  }, [ingredients, additionalCosts, showToast, copyToClipboard]);

  const handleIngredientChange = (
    id: string,
    field: keyof Omit<IngredientInput, 'id'>,
    value: string,
  ) => {
    setIngredients((prev) =>
      prev.map((ingredient) =>
        ingredient.id === id
          ? {
              ...applyIngredientUpdate(ingredient, field, value),
            }
          : ingredient,
      ),
    );
  };

  const addIngredientRow = () => {
    setIngredients((prev) => [...prev, createIngredient()]);
  };

  const removeIngredientRow = (id: string) => {
    setIngredients((prev) => {
      if (prev.length === 1) {
        showToast('Keep at least one ingredient', 'error');
        return prev;
      }
      return prev.filter((ingredient) => ingredient.id !== id);
    });
  };

  const addAdditionalCostItem = useCallback(() => {
    setAdditionalCosts((prev) => ({
      ...prev,
      items: [...prev.items, createAdditionalCostItem()],
    }));
  }, []);

  const removeAdditionalCostItem = useCallback((id: string) => {
    setAdditionalCosts((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }));
  }, []);

  const updateAdditionalCostItem = useCallback(
    (id: string, field: 'name' | 'amount', value: string) => {
      setAdditionalCosts((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.id === id
            ? {
                ...item,
                [field]:
                  field === 'name'
                    ? value
                    : Number.isNaN(Number(value))
                      ? 0
                      : Number(value),
              }
            : item,
        ),
      }));
    },
    [],
  );

  const updateYield = useCallback(
    (field: 'batchYield' | 'yieldUnit', value: string) => {
      setAdditionalCosts((prev) => ({
        ...prev,
        yield: {
          ...prev.yield,
          [field]:
            field === 'yieldUnit'
              ? value
              : Number.isNaN(Number(value))
                ? 0
                : Number(value),
        },
      }));
    },
    [],
  );

  const addMethodOfSale = useCallback(() => {
    setAdditionalCosts((prev) => ({
      ...prev,
      methodsOfSale: [...prev.methodsOfSale, createMethodOfSale()],
    }));
  }, []);

  const removeMethodOfSale = useCallback((id: string) => {
    setAdditionalCosts((prev) => ({
      ...prev,
      methodsOfSale: prev.methodsOfSale.filter((mos) => mos.id !== id),
    }));
  }, []);

  const updateMethodOfSale = useCallback(
    (id: string, field: 'quantity' | 'unit' | 'label', value: string) => {
      setAdditionalCosts((prev) => ({
        ...prev,
        methodsOfSale: prev.methodsOfSale.map((mos) =>
          mos.id === id
            ? {
                ...mos,
                [field]:
                  field === 'unit'
                    ? (value as UnitType)
                    : field === 'label'
                      ? value
                      : Number.isNaN(Number(value))
                        ? 0
                        : Number(value),
              }
            : mos,
        ),
      }));
    },
    [],
  );

  const resetCalculator = () => {
    setIngredients([createIngredient()]);
    setAdditionalCosts({
      items: [],
      yield: { batchYield: 1, yieldUnit: 'unit' },
      methodsOfSale: [],
    });
    localStorage.removeItem(STORAGE_KEY);
    setSearchParams({});
    setToast(null);
    showToast('Calculator reset', 'success');
  };

  const ingredientCost = useMemo(
    () =>
      ingredients.reduce((sum, ingredient) => sum + getLineCost(ingredient), 0),
    [ingredients],
  );

  const additionalCostTotal = useMemo(
    () => additionalCosts.items.reduce((sum, item) => sum + item.amount, 0),
    [additionalCosts.items],
  );

  const totalCost = useMemo(
    () => ingredientCost + additionalCostTotal,
    [ingredientCost, additionalCostTotal],
  );

  const unitCost = useMemo(() => {
    if (additionalCosts.yield.batchYield <= 0) return 0;
    return totalCost / additionalCosts.yield.batchYield;
  }, [additionalCosts.yield.batchYield, totalCost]);

  // Calculate cost per method of sale
  // If cost per yield unit is X, and we want cost per M units, it's X * M
  const methodsOfSaleCosts = useMemo(() => {
    if (additionalCosts.yield.batchYield <= 0 || totalCost <= 0) return [];
    return additionalCosts.methodsOfSale.map((mos) => {
      if (mos.quantity <= 0) {
        return { ...mos, costPerUnit: 0 };
      }
      // Cost per yield unit * quantity = cost per method of sale quantity
      const costPerMosUnit = unitCost * mos.quantity;
      return {
        ...mos,
        costPerUnit: costPerMosUnit,
      };
    });
  }, [
    additionalCosts.methodsOfSale,
    additionalCosts.yield.batchYield,
    unitCost,
    totalCost,
  ]);

  const perUnitHelper =
    additionalCosts.yield.batchYield > 0
      ? `Based on ${additionalCosts.yield.batchYield} ${additionalCosts.yield.yieldUnit || 'unit'}(s) produced`
      : 'Set batch yield above zero to calculate cost per unit';

  // Dynamic tooltip positioning handler
  const handleTooltipPosition = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const helpIcon = event.currentTarget;
      const tooltip = helpIcon.querySelector(
        `.${styles.tooltip}`,
      ) as HTMLElement;
      if (!tooltip) return;

      const iconRect = helpIcon.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 10; // Minimum distance from viewport edge

      // Temporarily show tooltip to measure it
      tooltip.style.visibility = 'hidden';
      tooltip.style.opacity = '0';
      tooltip.style.position = 'fixed';
      tooltip.style.top = '0';
      tooltip.style.left = '0';
      tooltip.style.display = 'block';
      const tooltipRect = tooltip.getBoundingClientRect();
      const tooltipWidth = tooltipRect.width;
      const tooltipHeight = tooltipRect.height;

      // Calculate available space
      const spaceAbove = iconRect.top;
      const spaceBelow = viewportHeight - iconRect.bottom;

      let top = 0;
      let left = 0;
      let isAbove = true;

      // Determine vertical position (prefer above, fallback to below)
      if (spaceAbove >= tooltipHeight + padding) {
        // Position above
        top = iconRect.top - tooltipHeight - padding;
        isAbove = true;
      } else if (spaceBelow >= tooltipHeight + padding) {
        // Position below
        top = iconRect.bottom + padding;
        isAbove = false;
      } else {
        // Not enough space, use whichever has more space
        if (spaceAbove > spaceBelow) {
          top = Math.max(padding, iconRect.top - tooltipHeight - padding);
          isAbove = true;
        } else {
          top = Math.min(
            viewportHeight - tooltipHeight - padding,
            iconRect.bottom + padding,
          );
          isAbove = false;
        }
      }

      // Determine horizontal position (center by default, adjust if needed)
      const iconCenterX = iconRect.left + iconRect.width / 2;
      left = iconCenterX - tooltipWidth / 2;

      // Adjust if tooltip goes off-screen horizontally
      if (left < padding) {
        left = padding;
      } else if (left + tooltipWidth > viewportWidth - padding) {
        left = viewportWidth - tooltipWidth - padding;
      }

      // Apply positioning
      tooltip.style.top = `${top}px`;
      tooltip.style.left = `${left}px`;
      tooltip.style.transform = 'none';
      tooltip.style.bottom = 'auto';
      tooltip.style.right = 'auto';

      // Update arrow position - calculate where arrow should point to icon
      const arrowLeft = iconCenterX - left;
      const arrowLeftPercent = (arrowLeft / tooltipWidth) * 100;
      const clampedArrowLeft = Math.max(15, Math.min(85, arrowLeftPercent));

      if (isAbove) {
        tooltip.classList.add(styles.tooltipAbove);
        tooltip.classList.remove(styles.tooltipBelow);
        tooltip.style.setProperty('--arrow-left', `${clampedArrowLeft}%`);
      } else {
        tooltip.classList.add(styles.tooltipBelow);
        tooltip.classList.remove(styles.tooltipAbove);
        tooltip.style.setProperty('--arrow-left', `${clampedArrowLeft}%`);
      }

      // Show tooltip
      tooltip.style.visibility = 'visible';
      tooltip.style.opacity = '1';
    },
    [],
  );

  const handleTooltipHide = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const helpIcon = event.currentTarget;
      const tooltip = helpIcon.querySelector(
        `.${styles.tooltip}`,
      ) as HTMLElement;
      if (tooltip) {
        tooltip.style.visibility = 'hidden';
        tooltip.style.opacity = '0';
      }
    },
    [],
  );

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Costing Calculator</h1>
          <p className={styles.subtitle}>
            Estimate the cost price of manufactured food by combining
            ingredients and production costs.
          </p>
          <div className={styles.headerActions}>
            <Button variant="secondary" onClick={resetCalculator}>
              Reset
            </Button>
            <Button variant="outline" onClick={handleShare}>
              <Share2 size={16} style={{ marginRight: '0.5rem' }} />
              Share
            </Button>
          </div>
        </div>

        <section className={styles.card}>
          <div className={styles.sectionHeader}>
            <h2>Ingredients</h2>
            <span className={styles.helperText}>
              Enter quantities and unit costs for each ingredient.
            </span>
          </div>
          <div className={styles.table}>
            {ingredients.map((ingredient) => {
              const conversionHint = getConversionHint(
                ingredient.quantity,
                ingredient.unit,
              );
              const lineCost = getLineCost(ingredient);

              return (
                <div className={styles.row} key={ingredient.id}>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>
                      <span>Name</span>
                      <div
                        className={styles.helpIcon}
                        onMouseEnter={handleTooltipPosition}
                        onMouseLeave={handleTooltipHide}
                      >
                        <HelpCircle size={14} />
                        <span className={styles.tooltip}>
                          Name of the ingredient (e.g., Flour, Sugar, Meat)
                        </span>
                      </div>
                    </label>
                    <input
                      type="text"
                      value={ingredient.name}
                      onChange={(event) =>
                        handleIngredientChange(
                          ingredient.id,
                          'name',
                          event.target.value,
                        )
                      }
                      placeholder="e.g., Flour"
                    />
                  </div>
                  <div
                    className={`${styles.fieldGroup} ${styles.fieldStacked}`}
                  >
                    <label className={styles.fieldLabel}>
                      <span>Quantity</span>
                      <div
                        className={styles.helpIcon}
                        onMouseEnter={handleTooltipPosition}
                        onMouseLeave={handleTooltipHide}
                      >
                        <HelpCircle size={14} />
                        <span className={styles.tooltip}>
                          Amount of ingredient used in the batch
                        </span>
                      </div>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ingredient.quantity}
                      onChange={(event) =>
                        handleIngredientChange(
                          ingredient.id,
                          'quantity',
                          event.target.value,
                        )
                      }
                      placeholder="0"
                    />
                    {conversionHint && (
                      <span className={styles.inlineHelper}>
                        {conversionHint}
                      </span>
                    )}
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>
                      <span>Unit</span>
                      <div
                        className={styles.helpIcon}
                        onMouseEnter={handleTooltipPosition}
                        onMouseLeave={handleTooltipHide}
                      >
                        <HelpCircle size={14} />
                        <span className={styles.tooltip}>
                          Unit of measurement (kg, g, lb, L, mL, or pcs)
                        </span>
                      </div>
                    </label>
                    <select
                      value={ingredient.unit}
                      onChange={(event) =>
                        handleIngredientChange(
                          ingredient.id,
                          'unit',
                          event.target.value,
                        )
                      }
                    >
                      {UNIT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div
                    className={`${styles.fieldGroup} ${styles.fieldStacked}`}
                  >
                    <label className={styles.fieldLabel}>
                      <span>Unit Cost</span>
                      <div
                        className={styles.helpIcon}
                        onMouseEnter={handleTooltipPosition}
                        onMouseLeave={handleTooltipHide}
                      >
                        <HelpCircle size={14} />
                        <span className={styles.tooltip}>
                          Cost per unit (e.g., cost per kg or per piece).
                          Entering this will automatically calculate Total Cost.
                        </span>
                      </div>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ingredient.unitCost}
                      onChange={(event) =>
                        handleIngredientChange(
                          ingredient.id,
                          'unitCost',
                          event.target.value,
                        )
                      }
                      placeholder="0"
                    />
                  </div>
                  <div
                    className={`${styles.fieldGroup} ${styles.fieldStacked}`}
                  >
                    <label className={styles.fieldLabel}>
                      <span>Total Cost</span>
                      <div
                        className={styles.helpIcon}
                        onMouseEnter={handleTooltipPosition}
                        onMouseLeave={handleTooltipHide}
                      >
                        <HelpCircle size={14} />
                        <span className={styles.tooltip}>
                          Total cost for this ingredient (e.g., if you bought
                          750g for ₱70). Entering this will automatically
                          calculate Unit Cost.
                        </span>
                      </div>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ingredient.totalCost}
                      onChange={(event) =>
                        handleIngredientChange(
                          ingredient.id,
                          'totalCost',
                          event.target.value,
                        )
                      }
                      placeholder="0"
                    />
                  </div>
                  <div className={`${styles.fieldGroup} ${styles.numericCell}`}>
                    <label className={styles.fieldLabel}>
                      <span>Line Cost</span>
                      <div
                        className={styles.helpIcon}
                        onMouseEnter={handleTooltipPosition}
                        onMouseLeave={handleTooltipHide}
                      >
                        <HelpCircle size={14} />
                        <span className={styles.tooltip}>
                          Calculated cost for this ingredient line (Quantity ×
                          Unit Cost or Total Cost)
                        </span>
                      </div>
                    </label>
                    <div className={styles.lineCostValue}>
                      ₱
                      {lineCost.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                  <div className={styles.actions}>
                    <Button
                      variant="ghost"
                      onClick={() => removeIngredientRow(ingredient.id)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              );
            })}
            <div className={styles.addIngredientRow}>
              <Button onClick={addIngredientRow} variant="outline">
                + Add Ingredient
              </Button>
            </div>
          </div>
        </section>

        {/* Additional Costs Section */}
        <section className={styles.card}>
          <div className={styles.sectionHeader}>
            <h2>Additional Costs</h2>
            <span className={styles.helperText}>
              Add any additional costs for the whole batch (e.g., packaging,
              labor, overhead, utilities).
            </span>
          </div>
          <div className={styles.additionalCostsList}>
            {additionalCosts.items.map((item) => (
              <div className={styles.additionalCostRow} key={item.id}>
                <div className={styles.field}>
                  <label>
                    <span>Cost Name</span>
                    <div
                      className={styles.helpIcon}
                      onMouseEnter={handleTooltipPosition}
                      onMouseLeave={handleTooltipHide}
                    >
                      <HelpCircle size={14} />
                      <span className={styles.tooltip}>
                        Name of the additional cost (e.g., Packaging, Labor,
                        Overhead)
                      </span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(event) =>
                      updateAdditionalCostItem(
                        item.id,
                        'name',
                        event.target.value,
                      )
                    }
                    placeholder="e.g., Packaging"
                  />
                </div>
                <div className={styles.field}>
                  <label>
                    <span>Amount</span>
                    <div
                      className={styles.helpIcon}
                      onMouseEnter={handleTooltipPosition}
                      onMouseLeave={handleTooltipHide}
                    >
                      <HelpCircle size={14} />
                      <span className={styles.tooltip}>
                        Total cost amount for this item in the batch
                      </span>
                    </div>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.amount}
                    onChange={(event) =>
                      updateAdditionalCostItem(
                        item.id,
                        'amount',
                        event.target.value,
                      )
                    }
                    placeholder="0"
                  />
                </div>
                <div className={styles.actions}>
                  <Button
                    variant="ghost"
                    onClick={() => removeAdditionalCostItem(item.id)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
            <div className={styles.addRow}>
              <Button onClick={addAdditionalCostItem} variant="outline">
                <Plus size={16} style={{ marginRight: '0.5rem' }} />
                Add Additional Cost
              </Button>
            </div>
          </div>
        </section>

        {/* Yield Section */}
        <section className={styles.card}>
          <div className={styles.sectionHeader}>
            <h2>Batch Yield</h2>
            <span className={styles.helperText}>
              Specify how many units this batch produces.
            </span>
          </div>
          <div className={styles.grid}>
            <div className={styles.field}>
              <label>
                <span>
                  Batch Yield
                  <div
                    className={styles.helpIcon}
                    onMouseEnter={handleTooltipPosition}
                    onMouseLeave={handleTooltipHide}
                  >
                    <HelpCircle size={14} />
                    <span className={styles.tooltip}>
                      Total number of units produced from this batch
                    </span>
                  </div>
                </span>
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={additionalCosts.yield.batchYield}
                onChange={(event) =>
                  updateYield('batchYield', event.target.value)
                }
                placeholder="1"
              />
              <span className={styles.helperText}>{perUnitHelper}</span>
            </div>
            <div className={styles.field}>
              <label>
                <span>
                  Yield Unit Label
                  <div
                    className={styles.helpIcon}
                    onMouseEnter={handleTooltipPosition}
                    onMouseLeave={handleTooltipHide}
                  >
                    <HelpCircle size={14} />
                    <span className={styles.tooltip}>
                      Label for the yield unit (e.g., piece, pack, bottle, kg)
                    </span>
                  </div>
                </span>
              </label>
              <input
                type="text"
                value={additionalCosts.yield.yieldUnit}
                onChange={(event) =>
                  updateYield('yieldUnit', event.target.value)
                }
                placeholder="e.g., piece, pack, bottle"
              />
            </div>
          </div>
        </section>

        {/* Methods of Sale Section */}
        <section className={styles.card}>
          <div className={styles.sectionHeader}>
            <h2>Methods of Sale</h2>
            <span className={styles.helperText}>
              Calculate cost if sold per specific quantity (e.g., per 400g or
              per 12 pieces).
            </span>
          </div>
          <div className={styles.methodsOfSaleList}>
            {additionalCosts.methodsOfSale.map((mos) => (
              <div className={styles.methodOfSaleRow} key={mos.id}>
                <div className={styles.field}>
                  <label>
                    <span>
                      Quantity
                      <div
                        className={styles.helpIcon}
                        onMouseEnter={handleTooltipPosition}
                        onMouseLeave={handleTooltipHide}
                      >
                        <HelpCircle size={14} />
                        <span className={styles.tooltip}>
                          Quantity per sale unit (e.g., 400 for 400g, 12 for 12
                          pieces)
                        </span>
                      </div>
                    </span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={mos.quantity}
                    onChange={(event) =>
                      updateMethodOfSale(mos.id, 'quantity', event.target.value)
                    }
                    placeholder="0"
                  />
                </div>
                <div className={styles.field}>
                  <label>
                    <span>
                      Unit
                      <div
                        className={styles.helpIcon}
                        onMouseEnter={handleTooltipPosition}
                        onMouseLeave={handleTooltipHide}
                      >
                        <HelpCircle size={14} />
                        <span className={styles.tooltip}>
                          Unit of measurement for this sale method
                        </span>
                      </div>
                    </span>
                  </label>
                  <select
                    value={mos.unit}
                    onChange={(event) =>
                      updateMethodOfSale(mos.id, 'unit', event.target.value)
                    }
                  >
                    {UNIT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.field}>
                  <label>
                    <span>
                      Label (Optional)
                      <div
                        className={styles.helpIcon}
                        onMouseEnter={handleTooltipPosition}
                        onMouseLeave={handleTooltipHide}
                      >
                        <HelpCircle size={14} />
                        <span className={styles.tooltip}>
                          Custom label for this sale method (e.g., "Per Pack")
                        </span>
                      </div>
                    </span>
                  </label>
                  <input
                    type="text"
                    value={mos.label || ''}
                    onChange={(event) =>
                      updateMethodOfSale(mos.id, 'label', event.target.value)
                    }
                    placeholder="e.g., Per Pack"
                  />
                </div>
                <div className={styles.field}>
                  <label>Cost per Sale Unit</label>
                  <div className={styles.numericCell}>
                    {(() => {
                      const mosCost = methodsOfSaleCosts.find(
                        (m) => m.id === mos.id,
                      );
                      return mosCost && mosCost.costPerUnit > 0 ? (
                        <>
                          ₱
                          {mosCost.costPerUnit.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </>
                      ) : (
                        '—'
                      );
                    })()}
                  </div>
                </div>
                <div className={styles.actions}>
                  <Button
                    variant="ghost"
                    onClick={() => removeMethodOfSale(mos.id)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
            <div className={styles.addRow}>
              <Button onClick={addMethodOfSale} variant="outline">
                <Plus size={16} style={{ marginRight: '0.5rem' }} />
                Add Method of Sale
              </Button>
            </div>
          </div>
        </section>

        <section className={styles.card}>
          <div className={styles.sectionHeader}>
            <h2>Cost Summary</h2>
            <span className={styles.helperText}>
              Review total and per-unit costs.
            </span>
          </div>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <p className={styles.summaryLabel}>Ingredients</p>
              <p className={styles.summaryValue}>
                ₱
                {ingredientCost.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </p>
            </div>
            <div className={styles.summaryCard}>
              <p className={styles.summaryLabel}>Additional Costs</p>
              <p className={styles.summaryValue}>
                ₱
                {additionalCostTotal.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </p>
            </div>
            <div className={styles.summaryCard}>
              <p className={styles.summaryLabel}>Total Batch Cost</p>
              <p className={styles.summaryValue}>
                ₱
                {totalCost.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </p>
            </div>
            <div className={styles.summaryCard}>
              <p className={styles.summaryLabel}>Cost per Unit</p>
              <p className={styles.summaryValue}>
                {additionalCosts.yield.batchYield > 0
                  ? `₱${unitCost.toLocaleString(undefined, { minimumFractionDigits: 2 })} per ${additionalCosts.yield.yieldUnit || 'unit'}`
                  : '—'}
              </p>
            </div>
            {methodsOfSaleCosts.length > 0 && (
              <>
                {methodsOfSaleCosts.map((mos) => (
                  <div className={styles.summaryCard} key={mos.id}>
                    <p className={styles.summaryLabel}>
                      Cost per {mos.quantity} {mos.unit}
                      {mos.label ? ` (${mos.label})` : ''}
                    </p>
                    <p className={styles.summaryValue}>
                      ₱
                      {mos.costPerUnit.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </p>
                  </div>
                ))}
              </>
            )}
          </div>
        </section>

        <section className={styles.feedbackSection}>
          <div className={styles.card}>
            <h2 className={styles.feedbackTitle}>Feedback & Support</h2>
            <p className={styles.feedbackDescription}>
              Have suggestions or encountered a problem? We'd love to hear from
              you!
            </p>
            <div className={styles.feedbackForm}>
              <textarea
                className={styles.feedbackInput}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Share your feedback, suggestions, or report any issues..."
                rows={4}
              />
              <Button
                onClick={handleSendFeedback}
                variant="primary"
                className={styles.feedbackButton}
              >
                Send Feedback via Email
              </Button>
            </div>
            <p className={styles.feedbackNote}>
              Your feedback will be sent to{' '}
              <span className={styles.feedbackEmailContainer}>
                <a
                  href="mailto:abraham.guerrero@abrasoft.com"
                  className={styles.feedbackEmail}
                >
                  abraham.guerrero@abrasoft.com
                </a>
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className={styles.copyEmailButton}
                  aria-label="Copy email address"
                  title="Copy email address"
                >
                  <Copy size={14} />
                </button>
              </span>
            </p>
          </div>
        </section>

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </>
  );
};

const applyIngredientUpdate = (
  ingredient: IngredientInput,
  field: keyof Omit<IngredientInput, 'id'>,
  rawValue: string,
): IngredientInput => {
  const parsedNumber = Number(rawValue);
  const numberValue = Number.isNaN(parsedNumber) ? 0 : parsedNumber;
  const updated: IngredientInput = { ...ingredient };

  switch (field) {
    case 'name':
      updated.name = rawValue;
      break;
    case 'unit':
      updated.unit = rawValue as UnitType;
      break;
    case 'quantity':
      updated.quantity = numberValue;
      if (updated.costMode === 'unit') {
        updated.totalCost = updated.unitCost * updated.quantity;
      } else {
        updated.unitCost =
          updated.quantity > 0 ? updated.totalCost / updated.quantity : 0;
      }
      break;
    case 'unitCost':
      updated.costMode = 'unit';
      updated.unitCost = numberValue;
      updated.totalCost = updated.unitCost * updated.quantity;
      break;
    case 'totalCost':
      updated.costMode = 'total';
      updated.totalCost = numberValue;
      updated.unitCost =
        updated.quantity > 0 ? updated.totalCost / updated.quantity : 0;
      break;
    default:
      break;
  }

  return updated;
};

const getLineCost = (ingredient: IngredientInput) => {
  if (ingredient.costMode === 'total') return ingredient.totalCost;
  return ingredient.unitCost * ingredient.quantity;
};

export default CostingCalculator;
